import { markProgrammaticPrint } from './programmaticPrintGuard';

// Paged.js paginates inside a hidden iframe rather than the main window so it
// can rewrite the document DOM without destroying the live UI. The iframe can't
// be a React component/portal — Paged.js must intercept DOMContentLoaded before
// any framework owns the DOM, which a React-rendered iframe would prevent.
//
// Paged.js polyfills CSS Paged Media margin boxes (@bottom-left, @bottom-right,
// string-set, counter(pages)) that browsers don't implement natively and that
// have no Tailwind equivalent, giving us running footers on every printed page.
//
// paged.polyfill.js lives in public/ rather than the npm package because it
// must load as a <script src> inside the iframe (not bundled), and the pagedjs
// package's exports field blocks subpath imports so `?url` imports also fail.
// To upgrade: copy node_modules/pagedjs/dist/paged.polyfill.js → public/.
const pagedPolyfillUrl = '/paged.polyfill.js';

// Styles reach the iframe in three ways:
//   1. <link> tags — absolute hrefs from the main document; Paged.js processes
//      normally. This is how Tailwind/app CSS (including document.css) arrives.
//   2. <style data-pagedjs-ignore> — Vite HMR inline styles have relative/blob
//      url() values that crash Paged.js's CSS walker; the attribute skips them.
//      The browser still renders them — only Paged.js's URL rewriting is skipped.
//   3. <style>${PAGE_CSS}</style> — our @page rules, no ignore attribute so
//      Paged.js processes them. CSS Paged Media properties have no Tailwind
//      equivalent and must be raw CSS.

const PAGE_CSS = `
  @page {
    size: letter;
    margin: 12mm 10mm 20mm 10mm;

    @bottom-left {
      content: string(doctitle);
      font-size: 9pt;
      color: #5B6770;
      font-family: Arial, Helvetica, sans-serif;
      white-space: nowrap;
      overflow: hidden;
      max-width: 60%;
    }

    @bottom-right {
      content: counter(page) " of " counter(pages);
      font-size: 9pt;
      color: #5B6770;
      font-family: Arial, Helvetica, sans-serif;
    }
  }

  /* Paged.js captures this element's text as the running footer title. */
  .tridion-pdf-title {
    string-set: doctitle content(text);
  }
`;
const buildPrintDocument = (
  contentHtml: string,
  stylesheetHrefs: string[],
  inlineStyles: string[],
  title: string,
  origin: string,
  lang: 'en' | 'es',
): string => {
  const linkTags = stylesheetHrefs
    .map(href => `<link rel="stylesheet" href="${href}">`)
    .join('\n');

  const styleTags = inlineStyles
    .map(css => `<style data-pagedjs-ignore>${css}</style>`)
    .join('\n');

  const afterCallback = `function() { window.print(); }`;

  return `<!DOCTYPE html>
<html lang="${lang}">
<head>
  <meta charset="UTF-8">
  <base href="${origin}/">
  <title>${escapeHtml(title)}</title>
  ${linkTags}
  ${styleTags}
  <style>${PAGE_CSS}</style>
  <script>
    window.PagedConfig = {
      auto: true,
      after: ${afterCallback}
    };
  </script>
  <script src="${pagedPolyfillUrl}" crossorigin="anonymous"></script>
</head>
<body>
  ${contentHtml}
</body>
</html>`;
};

/** Paginates the Tridion document via Paged.js and opens the print dialog. */
export const printTridionDocument = async (): Promise<void> => {
  const targetElement: HTMLElement | null = document.querySelector(
    '[data-tridion-content]',
  );

  if (!targetElement) {
    console.error('Tridion content element not found for printing');
    return;
  }

  // Button / Cmd+P path — suppress window `beforeprint` browser tracking.
  markProgrammaticPrint();

  const documentTitle =
    targetElement.dataset.tridionTitle ?? 'Training Document';

  const hiddenContainer = document.createElement('div');
  hiddenContainer.style.cssText =
    'position:absolute;left:-9999px;top:0;width:816px;';
  document.body.appendChild(hiddenContainer);

  try {
    const clonedElement = targetElement.cloneNode(true) as HTMLElement;
    hiddenContainer.appendChild(clonedElement);

    applyPrintModeTransformations(clonedElement, documentTitle);
    sanitizeClonedElement(clonedElement);
    clonedElement.classList.add('pdf-rendering');

    const stylesheetHrefs = Array.from(
      document.querySelectorAll<HTMLLinkElement>(
        'link[rel="stylesheet"]:not([data-pagedjs-ignore])',
      ),
    ).map(el => el.href);

    const inlineStyles = Array.from(
      document.querySelectorAll<HTMLStyleElement>(
        'style:not([data-pagedjs-ignore],[data-pagedjs-inserted-styles])',
      ),
    ).map(el => el.textContent);

    const htmlDoc = buildPrintDocument(
      clonedElement.outerHTML,
      stylesheetHrefs,
      inlineStyles,
      documentTitle,
      window.location.origin,
      targetElement.lang === 'es' ? 'es' : 'en',
    );

    hiddenContainer.remove();

    const iframe = document.createElement('iframe');
    iframe.style.cssText =
      'position:absolute;left:-9999px;top:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    const cleanup = () => iframe.remove();

    // Blob URL (not srcdoc) gives the iframe a valid base URL (blob:http://...
    // vs about:srcdoc) so Paged.js can resolve url() values without crashing.
    const blob = new Blob([htmlDoc], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);

    iframe.addEventListener('load', () => {
      URL.revokeObjectURL(blobUrl);
      const iframeWin = iframe.contentWindow;
      if (!iframeWin) {
        cleanup();
        return;
      }
      iframeWin.addEventListener('afterprint', () => {
        cleanup();
      });
    });

    iframe.src = blobUrl;
  } catch (error) {
    hiddenContainer.remove();
    console.error('Failed to render Tridion document for printing:', error);
  }
};

// Removes <script> elements and inline on* event-handler attributes from the
// cloned element before it is serialized into the iframe document, preventing
// XSS execution at parse time.
const sanitizeClonedElement = (element: HTMLElement): void => {
  element.querySelectorAll('script').forEach(el => el.remove());

  const eventHandlerPattern = /^on/i;
  const stripEventHandlers = (el: Element): void => {
    Array.from(el.attributes)
      .filter(attr => eventHandlerPattern.test(attr.name))
      .forEach(attr => el.removeAttribute(attr.name));
  };
  stripEventHandlers(element);
  element.querySelectorAll('*').forEach(el => stripEventHandlers(el));
};

// Strips [data-no-print] elements, replaces the sticky TridionTitle header
// with a plain h1.tridion-pdf-title for Paged.js's running footer string,
// and bakes sequential step numbers into the DOM (Paged.js's CSS counter
// rewriting breaks counter-increment on ::before pseudo-elements).
const applyPrintModeTransformations = (
  element: HTMLElement,
  title: string,
): void => {
  element.querySelectorAll('[data-no-print]').forEach(el => el.remove());

  element
    .querySelectorAll<HTMLOListElement>('ol[class~="steps"]')
    .forEach(ol => {
      ol.querySelectorAll<HTMLLIElement>(':scope > li[class~="step"]').forEach(
        (li, index) => {
          li.dataset.stepNumber = String(index + 1);
        },
      );
    });

  const titleWrapper = element.querySelector('[class*="shadow-elevation-16"]');

  if (titleWrapper) {
    const h1 = document.createElement('h1');
    h1.className = 'tridion-pdf-title';
    h1.style.cssText =
      'font-size: 32px; line-height: 1; color: #004c97; margin-bottom: 40px; padding-left: 40px; font-weight: bold;';
    h1.textContent = title;
    titleWrapper.replaceWith(h1);
  }
};

const escapeHtml = (str: string): string =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
